import type { TourScrollStrategy } from '@app/shared/tour';
import { config as breakpointConfig } from '@app/hooks/useBreakpoints';
import { scrollToHomeRecentUpdatesCommentPost } from './homeRecentUpdatesTourApi';

const TOUR_SCROLL_MS = 550;
const TOUR_SCROLL_PADDING_PX = 16;

const FEED_SCROLLER_SELECTOR = '[data-tour="home-recent-updates-scroller"]';

const animTokens = { home: 0, feed: 0 };

const getHomeScrollport = (): HTMLElement | null =>
  document.querySelector('[data-home-scrollport]');

const getRecentUpdatesScroller = (): HTMLElement | null => {
  const scroller = document.querySelector(FEED_SCROLLER_SELECTOR);
  return scroller instanceof HTMLElement ? scroller : null;
};

const canScrollY = (el: HTMLElement): boolean =>
  el.scrollHeight > el.clientHeight + 1;

const canScrollX = (el: HTMLElement): boolean =>
  el.scrollWidth > el.clientWidth + 1;

const canScrollFeed = (el: HTMLElement): boolean =>
  canScrollX(el) || canScrollY(el);

/**
 * Virtuoso's scroller is preferred (vertical on md+, horizontal on sm/xs).
 * Always use the designated feed scroller when present — never the home page.
 */
const findFeedScrollElement = (from: HTMLElement): HTMLElement | null => {
  const designated = getRecentUpdatesScroller();
  if (designated) {
    return designated;
  }

  const homeScrollport = getHomeScrollport();
  let node: HTMLElement | null = from;
  while (node && node !== document.body && node !== homeScrollport) {
    if (canScrollFeed(node)) {
      return node;
    }
    node = node.parentElement;
  }

  return null;
};

const RECENT_UPDATES_ACTION_STEP_IDS = new Set([
  'recent-updates-reactions',
  'recent-updates-comments',
]);

const NARROW_TOP_POPOVER_STEP_IDS = new Set([
  'recent-updates',
  'messages',
  'outlook',
  'upcoming-assignments',
  'recently-viewed',
  'starred',
  'quick-links',
  'manage',
]);

const TOUR_POPOVER_RESERVE_PX = 260;

type RectLike = {
  top: number;
  left: number;
  right: number;
  bottom: number;
};

const intersectRects = (a: RectLike, b: RectLike): RectLike => ({
  top: Math.max(a.top, b.top),
  left: Math.max(a.left, b.left),
  right: Math.min(a.right, b.right),
  bottom: Math.min(a.bottom, b.bottom),
});

const toDOMRect = (rect: RectLike): DOMRect => {
  const width = Math.max(0, rect.right - rect.left);
  const height = Math.max(0, rect.bottom - rect.top);
  return {
    ...rect,
    width,
    height,
    x: rect.left,
    y: rect.top,
    toJSON: () => ({}),
  } as DOMRect;
};

const getWindowViewportRect = (): RectLike => ({
  top: 0,
  left: 0,
  right: window.innerWidth,
  bottom: window.innerHeight,
});

/**
 * Visible clip for a Recent Updates CTA: feed scroller ∩ window ∩ home
 * scrollport. The scroller box can extend below the window (sticky sidebar
 * height vs chrome), so scroller bounds alone are not "on screen".
 */
export const getRecentUpdatesVisibleClip = (scroller: HTMLElement): DOMRect => {
  let clip = intersectRects(
    scroller.getBoundingClientRect(),
    getWindowViewportRect()
  );
  const scrollport = getHomeScrollport();
  if (scrollport) {
    clip = intersectRects(clip, scrollport.getBoundingClientRect());
  }
  return toDOMRect(clip);
};

const isRectFullyInView = (rect: DOMRect, view: DOMRect): boolean =>
  rect.width > 0 &&
  rect.height > 0 &&
  view.width > 0 &&
  view.height > 0 &&
  rect.top >= view.top - 2 &&
  rect.bottom <= view.bottom + 2 &&
  rect.left >= view.left - 2 &&
  rect.right <= view.right + 2;

/** Fully visible with padding — avoids leaving CTAs cramped at the clip edge. */
export const isRectComfortablyInView = (
  rect: DOMRect,
  view: DOMRect,
  padding = TOUR_SCROLL_PADDING_PX
): boolean =>
  isRectFullyInView(rect, view) &&
  rect.top >= view.top + padding - 2 &&
  rect.bottom <= view.bottom - padding + 2 &&
  rect.left >= view.left + padding - 2 &&
  rect.right <= view.right - padding + 2;

export const isRectPartiallyInView = (rect: DOMRect, view: DOMRect): boolean =>
  rect.width > 0 &&
  rect.height > 0 &&
  view.width > 0 &&
  view.height > 0 &&
  rect.bottom > view.top + 4 &&
  rect.top < view.bottom - 4 &&
  rect.right > view.left + 4 &&
  rect.left < view.right - 4;

const getTourViewportTarget = (
  element: Element | undefined,
  stepId?: string
): Element | null => {
  if (stepId === 'spaces') {
    return document.querySelector('#home-space-widget');
  }
  if (stepId?.startsWith('space-') && element) {
    return element.closest('article') ?? element;
  }
  return element ?? null;
};

const isElementFittedInHomeView = (
  element: Element | null | undefined,
  scrollport: HTMLElement | null
): boolean => {
  if (!(element instanceof HTMLElement)) {
    return true;
  }

  const rect = element.getBoundingClientRect();
  const port = scrollport?.getBoundingClientRect() ?? {
    top: 0,
    bottom: window.innerHeight,
    height: window.innerHeight,
  };
  const portHeight = port.height ?? port.bottom - port.top;
  const padding = TOUR_SCROLL_PADDING_PX;

  if (rect.height > portHeight - padding * 2) {
    return rect.top >= port.top - 2 && rect.top <= port.top + padding * 2;
  }

  return rect.top >= port.top - 2 && rect.bottom <= port.bottom + 2;
};

const getHomeScrollTopForTarget = (
  target: HTMLElement,
  scrollport: HTMLElement,
  stepId?: string
): number => {
  const targetRect = target.getBoundingClientRect();
  const portRect = scrollport.getBoundingClientRect();
  const padding = TOUR_SCROLL_PADDING_PX;
  const isNarrow = window.innerWidth < breakpointConfig.md;

  // Full-width sm widgets have no left/right room for the popover. Pin the
  // widget below a reserved band so driver.js can place it on `top`.
  if (isNarrow && stepId && NARROW_TOP_POPOVER_STEP_IDS.has(stepId)) {
    const delta = targetRect.top - (portRect.top + TOUR_POPOVER_RESERVE_PX);
    return Math.max(0, scrollport.scrollTop + delta);
  }

  const delta =
    targetRect.height > portRect.height - padding * 2
      ? targetRect.top - (portRect.top + padding)
      : targetRect.top +
        targetRect.height / 2 -
        (portRect.top + portRect.height / 2);

  return Math.max(0, scrollport.scrollTop + delta);
};

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2;

const animateElementScrollTo = (
  element: HTMLElement,
  to: { top?: number; left?: number },
  channel: 'home' | 'feed',
  durationMs = TOUR_SCROLL_MS
): void => {
  const fromTop = element.scrollTop;
  const fromLeft = element.scrollLeft;
  const targetTop = Math.max(0, to.top ?? fromTop);
  const targetLeft = Math.max(0, to.left ?? fromLeft);
  const deltaTop = targetTop - fromTop;
  const deltaLeft = targetLeft - fromLeft;
  if (Math.abs(deltaTop) < 1 && Math.abs(deltaLeft) < 1) {
    return;
  }

  const token = ++animTokens[channel];

  // SnapScroller uses scroll-behavior:smooth — disable it so rAF easing
  // is not double-smoothed on every frame.
  const prevBehavior = element.style.scrollBehavior;
  const prevSnap = element.style.scrollSnapType;
  element.style.scrollBehavior = 'auto';
  element.style.scrollSnapType = 'none';
  const startedAt = performance.now();

  const frame = (now: number) => {
    if (token !== animTokens[channel]) {
      element.style.scrollBehavior = prevBehavior;
      element.style.scrollSnapType = prevSnap;
      return;
    }
    const progress = Math.min(1, (now - startedAt) / durationMs);
    const eased = easeInOutCubic(progress);
    if (to.top !== undefined) {
      element.scrollTop = fromTop + deltaTop * eased;
    }
    if (to.left !== undefined) {
      element.scrollLeft = fromLeft + deltaLeft * eased;
    }
    if (progress < 1) {
      window.requestAnimationFrame(frame);
      return;
    }
    element.style.scrollBehavior = prevBehavior;
    element.style.scrollSnapType = prevSnap;
  };

  window.requestAnimationFrame(frame);
};

const animateHomeScrollportTo = (
  scrollport: HTMLElement,
  top: number,
  durationMs = TOUR_SCROLL_MS
): void => {
  animateElementScrollTo(scrollport, { top }, 'home', durationMs);
};

const scrollSpaceCarouselToElement = (element: Element | undefined): void => {
  if (!(element instanceof HTMLElement)) {
    return;
  }

  const slide = element.closest('.swiper-slide') as HTMLElement | null;
  const wrapper = slide?.closest('.swiper-wrapper') as HTMLElement | null;
  const container = wrapper?.closest('.swiper') as HTMLElement | null;

  if (!slide || !container) {
    return;
  }

  const swiper = (container as any)?.swiper;
  if (!swiper || typeof swiper.slideTo !== 'function') {
    return;
  }

  const slides = Array.from(wrapper!.children);
  const slideIndex = slides.indexOf(slide);
  if (slideIndex < 0) {
    return;
  }

  const containerRect = container.getBoundingClientRect();
  const slideRect = slide.getBoundingClientRect();
  const isFullyVisible =
    slideRect.left >= containerRect.left - 2 &&
    slideRect.right <= containerRect.right + 2;

  if (!isFullyVisible) {
    swiper.slideTo(slideIndex, TOUR_SCROLL_MS);
  }
};

const withInstantFeedScroll = (
  scroller: HTMLElement,
  mutate: () => void
): void => {
  // SnapScroller sets scroll-behavior:smooth; override so the highlight
  // measures after the CTA is already in the clip.
  const prevBehavior = scroller.style.scrollBehavior;
  const prevSnap = scroller.style.scrollSnapType;
  scroller.style.scrollBehavior = 'auto';
  scroller.style.scrollSnapType = 'none';
  mutate();
  scroller.style.scrollBehavior = prevBehavior;
  scroller.style.scrollSnapType = prevSnap;
};

/**
 * Virtuoso only mounts cards near the scroll offset. Step through the feed
 * (vertical or horizontal) until a tour target exists in the DOM — e.g. a
 * comment icon on a Common room card past several Announcement-only cards.
 */
export const revealRecentUpdatesTourTarget = (
  widget: Element,
  selector: string
): Element | null => {
  const existing = widget.querySelector(selector);
  if (existing) {
    return existing;
  }

  const scroller = widget.querySelector(FEED_SCROLLER_SELECTOR);
  if (!(scroller instanceof HTMLElement)) {
    return null;
  }

  const maxTop = Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  const maxLeft = Math.max(0, scroller.scrollWidth - scroller.clientWidth);
  if (maxTop < 1 && maxLeft < 1) {
    return null;
  }

  const startTop = scroller.scrollTop;
  const startLeft = scroller.scrollLeft;
  const stepY = Math.max(100, Math.floor(scroller.clientHeight * 0.7) || 100);
  const stepX = Math.max(100, Math.floor(scroller.clientWidth * 0.7) || 100);

  const tryHit = (): Element | null => {
    void scroller.offsetHeight;
    return widget.querySelector(selector);
  };

  if (maxLeft >= 1) {
    for (let left = 0; left <= maxLeft; left += stepX) {
      withInstantFeedScroll(scroller, () => {
        scroller.scrollLeft = left;
      });
      const hit = tryHit();
      if (hit) {
        return hit;
      }
    }
  }

  if (maxTop >= 1) {
    for (let top = 0; top <= maxTop; top += stepY) {
      withInstantFeedScroll(scroller, () => {
        scroller.scrollTop = top;
      });
      const hit = tryHit();
      if (hit) {
        return hit;
      }
    }
  }

  withInstantFeedScroll(scroller, () => {
    scroller.scrollTop = startTop;
    scroller.scrollLeft = startLeft;
  });
  return null;
};

/**
 * Scroll the feed only when a reaction/comment CTA is clipped or off-screen.
 * If the icon is already readable in the clip (e.g. on the first card), leave
 * the post in place so the user can still read it. When we do scroll, move
 * the minimum amount — like the Spaces carousel — so the user sees the feed
 * travel to a target that was further down.
 */
export const scrollRecentUpdatesCtaIntoView = (element: Element): boolean => {
  if (!(element instanceof HTMLElement)) {
    return false;
  }

  const scroller = findFeedScrollElement(element);
  if (!scroller) {
    return false;
  }

  const clip = getRecentUpdatesVisibleClip(scroller);
  const rect = element.getBoundingClientRect();
  const padding = TOUR_SCROLL_PADDING_PX;

  if (clip.height <= 0 || clip.width <= 0) {
    return false;
  }

  if (isRectComfortablyInView(rect, clip, padding)) {
    return false;
  }

  let deltaTop = 0;
  if (rect.top < clip.top + padding) {
    deltaTop = rect.top - (clip.top + padding);
  } else if (rect.bottom > clip.bottom - padding) {
    deltaTop = rect.bottom - (clip.bottom - padding);
  }

  let deltaLeft = 0;
  if (rect.left < clip.left + padding) {
    deltaLeft = rect.left - (clip.left + padding);
  } else if (rect.right > clip.right - padding) {
    deltaLeft = rect.right - (clip.right - padding);
  }

  if (Math.abs(deltaTop) < 1 && Math.abs(deltaLeft) < 1) {
    return false;
  }

  const maxTop = scroller.scrollHeight - scroller.clientHeight;
  const unclampedTop = scroller.scrollTop + deltaTop;
  const nextTop =
    maxTop > 0
      ? Math.min(maxTop, Math.max(0, unclampedTop))
      : Math.max(0, unclampedTop);

  const maxLeft = scroller.scrollWidth - scroller.clientWidth;
  const unclampedLeft = scroller.scrollLeft + deltaLeft;
  const nextLeft =
    maxLeft > 0
      ? Math.min(maxLeft, Math.max(0, unclampedLeft))
      : Math.max(0, unclampedLeft);

  const canY = canScrollY(scroller) || scroller.clientHeight === 0;
  const canX = canScrollX(scroller) || scroller.clientWidth === 0;
  const willScrollY = canY && Math.abs(nextTop - scroller.scrollTop) >= 1;
  const willScrollX = canX && Math.abs(nextLeft - scroller.scrollLeft) >= 1;
  if (!willScrollY && !willScrollX) {
    return false;
  }

  animateElementScrollTo(
    scroller,
    {
      ...(willScrollY ? { top: nextTop } : {}),
      ...(willScrollX ? { left: nextLeft } : {}),
    },
    'feed'
  );
  return true;
};

/** Disable CSS scroll-snap while Virtuoso scrollToIndex animates. */
export const pauseRecentUpdatesFeedSnap = (
  durationMs = TOUR_SCROLL_MS
): void => {
  const scroller = getRecentUpdatesScroller();
  if (!scroller) {
    return;
  }
  const prevBehavior = scroller.style.scrollBehavior;
  const prevSnap = scroller.style.scrollSnapType;
  scroller.style.scrollBehavior = 'auto';
  scroller.style.scrollSnapType = 'none';
  window.setTimeout(() => {
    scroller.style.scrollBehavior = prevBehavior;
    scroller.style.scrollSnapType = prevSnap;
  }, durationMs);
};

const WIDGET_ON_SCREEN_PX = 80;

const isRecentUpdatesWidgetOnScreen = (
  widget: HTMLElement,
  scrollport: HTMLElement | null
): boolean => {
  const rect = widget.getBoundingClientRect();
  const port = scrollport?.getBoundingClientRect() ?? {
    top: 0,
    bottom: window.innerHeight,
  };
  const overlap =
    Math.min(rect.bottom, port.bottom) - Math.max(rect.top, port.top);
  return overlap > WIDGET_ON_SCREEN_PX;
};

export const scrollHomeToTourTarget: TourScrollStrategy = (element, stepId) => {
  if (stepId?.startsWith('space-') && stepId !== 'spaces') {
    scrollSpaceCarouselToElement(element);
  }

  if (
    stepId &&
    RECENT_UPDATES_ACTION_STEP_IDS.has(stepId) &&
    element instanceof Element
  ) {
    const widget = document.querySelector('#home-recent-updates-widget');
    const scrollport = getHomeScrollport();
    let didPage = false;
    if (
      widget instanceof HTMLElement &&
      !isRecentUpdatesWidgetOnScreen(widget, scrollport)
    ) {
      if (scrollport) {
        animateHomeScrollportTo(
          scrollport,
          getHomeScrollTopForTarget(widget, scrollport),
          TOUR_SCROLL_MS
        );
        didPage = true;
      } else {
        widget.scrollIntoView({
          block: 'nearest',
          inline: 'nearest',
          behavior: 'smooth',
        });
        didPage = true;
      }
    }

    let didFeed = false;
    if (stepId === 'recent-updates-comments') {
      const scroller = findFeedScrollElement(
        element instanceof HTMLElement ? element : document.body
      );
      const clip = scroller ? getRecentUpdatesVisibleClip(scroller) : null;
      const rect = (element as HTMLElement).getBoundingClientRect();
      const comfortable = clip != null && isRectComfortablyInView(rect, clip);
      const partial = clip != null && isRectPartiallyInView(rect, clip);
      if (!comfortable) {
        didFeed = scrollRecentUpdatesCtaIntoView(element);
        if (!didFeed && !partial) {
          didFeed = scrollToHomeRecentUpdatesCommentPost({ smooth: true });
        }
      }
    } else {
      didFeed = scrollRecentUpdatesCtaIntoView(element);
    }
    return didPage || didFeed;
  }

  const target = getTourViewportTarget(element, stepId);
  if (!(target instanceof HTMLElement)) {
    return false;
  }

  const scrollport = getHomeScrollport();
  const needsPopoverRoom =
    window.innerWidth < breakpointConfig.md &&
    Boolean(stepId && NARROW_TOP_POPOVER_STEP_IDS.has(stepId));

  if (!needsPopoverRoom && isElementFittedInHomeView(target, scrollport)) {
    return false;
  }

  if (!scrollport) {
    if (isElementFittedInHomeView(target, null)) {
      return false;
    }
    target.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: 'smooth',
    });
    return true;
  }

  const nextTop = getHomeScrollTopForTarget(target, scrollport, stepId);
  if (Math.abs(nextTop - scrollport.scrollTop) < 1) {
    return false;
  }

  animateHomeScrollportTo(scrollport, nextTop, TOUR_SCROLL_MS);
  return true;
};

export const scrollHomeToTop = (): void => {
  const scrollport = getHomeScrollport();
  if (scrollport && scrollport.scrollTop >= 1) {
    animateHomeScrollportTo(scrollport, 0, TOUR_SCROLL_MS);
  }

  const feed = getRecentUpdatesScroller();
  if (feed && (feed.scrollTop >= 1 || feed.scrollLeft >= 1)) {
    animateElementScrollTo(feed, { top: 0, left: 0 }, 'feed');
  }
};

export const cancelHomeScrollAnim = (): void => {
  animTokens.home += 1;
  animTokens.feed += 1;
};
